import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useSession } from '@/lib/session';
import { LogoMark } from './Logo';
import { BRAND } from '@/brand';

export const FullScreenLoader = ({ label = `Loading ${BRAND.name}` }: { readonly label?: string }) => (
  <div className="flex h-dvh items-center justify-center bg-background" role="status" aria-live="polite">
    <LogoMark className="size-10 animate-pulse" />
    <span className="sr-only">{label}</span>
  </div>
);

export interface RequireAuthProps {
  readonly children: ReactNode;
  /** Require at least one entity (else → onboarding). Default true. */
  readonly requireEntity?: boolean;
}

/** Gate for authenticated routes: loading → splash; signed out → /login?next=…; no entity → onboarding. */
export const RequireAuth = ({ children, requireEntity = true }: RequireAuthProps) => {
  const { status, memberships } = useSession();
  const location = useLocation();
  if (status === 'loading') return <FullScreenLoader />;
  if (status === 'anonymous') {
    const next = location.pathname + location.search;
    return <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />;
  }
  // nothing set up yet → the checklist; otherwise never blocked (unfinished steps wait on Home's checklist card)
  if (requireEntity && memberships.length === 0) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
};
